import axios, { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios';
import { RootStore } from '../app/store';
import { sendNetworkCallLogs } from '../reducers/messagingSlice';
import appContainer from './container';
import { PerfTimer } from './timer';
import {
  backoffMs,
  GATEWAY_TIMEOUT_MS,
  isRetryableStatus,
  sleep,
} from './retry';
import logger from './logger';

enum HTTPMethods {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  patch = 'PATCH',
  remove = 'DELETE',
}

interface IParams {
  url: string;
  data?: any;
  headers?: RawAxiosRequestHeaders;
  successCallback?: (data: any) => void;
  errorCallback?: (message: string, statusCode?: number) => void;
  /**
   * Per-attempt timeout, defaulting to GATEWAY_TIMEOUT_MS. That is a loose
   * bound, not a free one: the client's clock starts before the gateway's, so
   * an abort here can still land on a request the backend went on to accept.
   * Pass 0 wherever that matters more than the hang does — `timeoutMs: 0` on
   * the order post in `network.ts` is the reason this option exists.
   */
  timeoutMs?: number;
  /**
   * HITL-1800. Total attempts including the first — 1 (the default) keeps every
   * existing caller single-shot. Opt in only where a resend is safe: these
   * wrappers also carry order submissions and event posts, and an automatic
   * duplicate final order is its own incident class (HITL-1513).
   */
  attempts?: number;
}

const DEFAULT_HEADERS = {
  Accept: 'application/json',
};

/**
 * One axios request, resent on a transient failure only: a timeout or network
 * error (no `response`), or a retryable status. A 401/403 is returned to the
 * caller untouched — that is what lets auth tell a genuinely dead token from a
 * casualty of an outage.
 */
const requestWithRetry = async (
  requestConfig: AxiosRequestConfig,
  attempts: number,
  url: string
) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await axios(requestConfig);
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      const isTransient = status === undefined || isRetryableStatus(status);

      if (attempt >= attempts || !isTransient) throw error;

      logger.warn({
        message: `Retrying api request after ${status ?? 'network error'}`,
        moreInfo: { url, attempt, attempts },
      });
      await sleep(backoffMs(attempt));
    }
  }
};

const apiCall = async (method: HTTPMethods, params: IParams) => {
  const {
    data,
    url,
    headers,
    successCallback,
    errorCallback,
    timeoutMs = GATEWAY_TIMEOUT_MS,
    attempts = 1,
  } = params || {};
  const requestTimer = new PerfTimer();

  const store = appContainer.get<RootStore>('store')!;

  try {
    const response = await requestWithRetry(
      {
        headers: {
          ...DEFAULT_HEADERS,
          ...headers,
        },
        method,
        url,
        // Without this a connection stalled at the API Gateway edge hangs for
        // the browser default (~300s) rather than failing. 0 means axios leaves
        // it unbounded, which is what the order post asks for.
        timeout: timeoutMs,
        ...(method !== HTTPMethods.get && { data }),
      },
      attempts,
      url
    );

    store.dispatch(
      sendNetworkCallLogs({
        url,
        duration: requestTimer.stop(),
        message: 'api request succeeded',
        via: 'axios_api_wrapper',
      })
    );

    successCallback?.(response);

    return response;
  } catch (error) {
    const errorDetails = axios.isAxiosError(error)
      ? {
          message: error.message,
          status: error.response?.status ?? error.status,
          code: error.code,
          responseData: JSON.stringify(error.response?.data),
        }
      : { message: 'An unexpected error occurred', status: 500 };

    store.dispatch(
      sendNetworkCallLogs({
        url,
        duration: requestTimer.stop(),
        message: `api request failed with error message: ${errorDetails.message}, http status: ${errorDetails.status}, error code: ${errorDetails.code}, response data: ${errorDetails.responseData}`,
        via: 'axios_api_wrapper',
      })
    );

    errorCallback?.(errorDetails.message, errorDetails.status);

    throw error;
  }
};

const apiCallWrapper = (method: HTTPMethods) => (params: IParams) =>
  apiCall(method, params);

const get = apiCallWrapper(HTTPMethods.get);
const post = apiCallWrapper(HTTPMethods.post);
const put = apiCallWrapper(HTTPMethods.put);
const patch = apiCallWrapper(HTTPMethods.patch);
const remove = apiCallWrapper(HTTPMethods.remove);

export { get, patch, post, put, remove };
